import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import { useLogin } from "@/hooks/useAuth";

export function LoginPage() {
  const navigate = useNavigate();
  const { mutate, isPending, error } = useLogin();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    mutate(
      { username, password },
      { onSuccess: () => navigate("/", { replace: true }) }
    );
  }

  return (
    <>
      <Helmet>
        <title>Log in — e621 Animation Finder</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-surface-0 flex items-center justify-center px-6">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-sm space-y-4 bg-surface-1 border border-surface-3 rounded-lg p-6"
        >
          <h1 className="text-xl font-bold">Log in</h1>
          <p className="text-xs text-zinc-500">
            Sign in to sync your blacklist, save searches, and access admin
            functions if you have them.
          </p>

          <Field label="Username">
            <input
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
              className={inputClass}
            />
          </Field>

          <Field label="Password">
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className={inputClass}
            />
          </Field>

          {error && (
            <p className="text-xs text-red-400">{(error as Error).message}</p>
          )}

          <button
            type="submit"
            disabled={isPending}
            className="w-full bg-accent hover:bg-accent-hover disabled:opacity-50 text-white font-semibold py-2 rounded transition-colors"
          >
            {isPending ? "Signing in…" : "Sign in"}
          </button>

          <p className="text-xs text-zinc-500 text-center">
            No account?{" "}
            <Link to="/register" className="text-accent-light hover:underline">
              Register
            </Link>
            {" · "}
            <Link to="/" className="text-zinc-600 hover:text-zinc-400">
              Continue as guest
            </Link>
          </p>
        </form>
      </div>
    </>
  );
}

const inputClass =
  "w-full bg-surface-2 border border-surface-3 rounded px-3 py-2 text-sm focus:outline-none focus:border-accent transition-colors";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
